import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, FileText, ArrowRight, RotateCcw, Landmark, FlaskConical, Flag, Star, Flame } from "lucide-react";
import { toast } from "sonner";
import { listInvestors } from "../lib/api";
import {
  PHASES,
  BETREUER,
  EINSTUFUNGEN,
  INVESTORENTYP_KATEGORIEN,
} from "../components/investors/Badges";
import InvestorTable from "../components/investors/InvestorTable";
import InvestorDrawer from "../components/investors/InvestorDrawer";
import BerichtModal from "../components/reports/BerichtModal";
import PageHeader from "../components/layout/PageHeader";

const EINSTUFUNG_DOT = {
  Heiss: "bg-heiss",
  Reaktivierbar: "bg-reaktiv",
  Nachrang: "bg-nachrang",
  Tot: "bg-tot",
};

const AFFINITAET_LEVELS = ["hoch", "mittel", "niedrig"];

const QUICK_CHIPS = [
  { id: "ebr",   label: "Erbbau-Spezialisten", icon: Landmark,      field: "ebr_aff",     value: "hoch" },
  { id: "ls",    label: "Life Science-Fokus",  icon: FlaskConical,  field: "ls_aff",      value: "hoch" },
  { id: "de",    label: "Deutschland-aktiv",   icon: Flag,          field: "de_aff",      value: "hoch" },
  { id: "tier1", label: "Tier 1",              icon: Star,          field: "tier",        value: "1"    },
  { id: "heiss", label: "Heiß",                icon: Flame,         field: "einstufung",  value: "Heiss"},
];

export default function TopKandidaten() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 100 });
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState(params.get("search") || "");
  const [kategorie, setKategorie] = useState(params.get("investorentyp_kategorie") || "");
  const [einstufung, setEinstufung] = useState(params.get("einstufung") || "");
  const [phase, setPhase] = useState(params.get("phase") || "");
  const [quelle, setQuelle] = useState(params.get("quelle") || "");
  const [betreuer, setBetreuer] = useState(params.get("betreuer") || "");
  const [ebrAff, setEbrAff] = useState(params.get("ebr_aff") || "");
  const [lsAff, setLsAff]   = useState(params.get("ls_aff")  || "");
  const [deAff, setDeAff]   = useState(params.get("de_aff")  || "");
  const [tier, setTier]     = useState(params.get("tier")    || "");
  const [page, setPage] = useState(Number(params.get("page") || 1));

  const isDefaultMode = !einstufung && !phase && !ebrAff && !lsAff && !deAff && !tier;
  const [drawerId, setDrawerId] = useState(null);
  const [berichtOpen, setBerichtOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = {
        page,
        page_size: 100,
        sort_by: "tier",
        sort_dir: "asc",
        search: search || undefined,
        investorentyp_kategorie: kategorie || undefined,
        einstufung: einstufung || undefined,
        phase: phase || undefined,
        quelle: quelle || undefined,
        betreuer: betreuer || undefined,
        ebr_affinitaet: ebrAff || undefined,
        ls_affinitaet: lsAff || undefined,
        de_affinitaet: deAff || undefined,
        tier: tier ? Number(tier) : undefined,
      };
      if (isDefaultMode) {
        q.einstufung_in = "Heiss,Reaktivierbar";
        q.phase_ne = "Abgesagt";
      }
      const list = await listInvestors(q);
      setData(list);
    } catch {
      toast.error("Top-Kandidaten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [page, search, kategorie, einstufung, phase, quelle, betreuer, ebrAff, lsAff, deAff, tier, isDefaultMode]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const p = {};
    if (search) p.search = search;
    if (kategorie) p.investorentyp_kategorie = kategorie;
    if (einstufung) p.einstufung = einstufung;
    if (phase) p.phase = phase;
    if (quelle) p.quelle = quelle;
    if (betreuer) p.betreuer = betreuer;
    if (ebrAff) p.ebr_aff = ebrAff;
    if (lsAff) p.ls_aff = lsAff;
    if (deAff) p.de_aff = deAff;
    if (tier) p.tier = tier;
    if (page > 1) p.page = String(page);
    setParams(p, { replace: true });
  }, [search, kategorie, einstufung, phase, quelle, betreuer, ebrAff, lsAff, deAff, tier, page, setParams]);

  const resetFilters = () => {
    setSearch(""); setKategorie(""); setEinstufung(""); setPhase("");
    setQuelle(""); setBetreuer(""); setEbrAff(""); setLsAff(""); setDeAff("");
    setTier(""); setPage(1);
  };
  const hasFilters = search || kategorie || einstufung || phase || quelle || betreuer || ebrAff || lsAff || deAff || tier;

  // Quick-Chip Toggle
  const chipSetters = { ebr_aff: setEbrAff, ls_aff: setLsAff, de_aff: setDeAff, tier: setTier, einstufung: setEinstufung };
  const chipValues  = { ebr_aff: ebrAff, ls_aff: lsAff, de_aff: deAff, tier: tier, einstufung: einstufung };
  const toggleChip = (chip) => {
    setPage(1);
    const current = chipValues[chip.field];
    const setter = chipSetters[chip.field];
    setter(current === chip.value ? "" : chip.value);
  };
  const isChipActive = (chip) => chipValues[chip.field] === chip.value;

  return (
    <div data-testid="top-kandidaten-page" className="pb-container px-0 sm:px-4 lg:px-8 py-4 sm:py-8 space-y-6">
      <PageHeader
        eyebrow="Newton Campus · Investoren-Akquise"
        title="Top-Kandidaten"
        subtitle="Kuratierte Liste der aktiven Investoren — heiß und reaktivierbar, sortiert nach Tier-Match. Inline-Edits werden protokolliert."
        testid="top-kandidaten-header"
        actions={
          <>
            <button
              data-testid="open-bericht-btn"
              onClick={() => setBerichtOpen(true)}
              className="pb-btn-accent text-xs uppercase tracking-widish font-semibold min-h-[40px]"
              title="Excel-Komplettexport aller Investoren"
            >
              <FileText size={13} /> <span className="hidden sm:inline">Investoren-Export</span><span className="sm:hidden">Export</span>
            </button>
            <a
              href="/investoren"
              data-testid="goto-pipeline-btn"
              className="pb-btn-primary text-xs uppercase tracking-widish font-semibold min-h-[40px]"
            >
              <ArrowRight size={13} /> <span className="hidden sm:inline">Zur Pipeline</span><span className="sm:hidden">Pipeline</span>
            </a>
          </>
        }
      />

      {/* Quick-Filter-Chips */}
      <section className="bg-white border border-navy/15 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="tk-quick-chips">
        <div className="flex flex-wrap items-center gap-2">
          <span className="pb-eyebrow text-navy/55 mr-1">Quick-Filter:</span>
          {QUICK_CHIPS.map((chip) => {
            const Icon = chip.icon;
            const active = isChipActive(chip);
            return (
              <button
                key={chip.id}
                type="button"
                data-testid={`chip-${chip.id}`}
                onClick={() => toggleChip(chip)}
                aria-pressed={active}
                className={
                  "inline-flex items-center gap-1.5 text-xs font-sans uppercase tracking-widish px-3 py-1.5 min-h-[36px] border transition-colors " +
                  (active
                    ? "border-gold text-white bg-gold"
                    : "border-navy/20 text-navy/75 hover:border-gold hover:text-gold bg-white")
                }
              >
                <Icon size={13} strokeWidth={1.8} /> {chip.label}
              </button>
            );
          })}
        </div>
        {hasFilters && (
          <button
            type="button"
            data-testid="tk-chip-reset"
            onClick={resetFilters}
            className="text-xs uppercase tracking-widish text-navy/65 hover:text-gold inline-flex items-center gap-1.5 font-sans"
          >
            <RotateCcw size={12} /> Alle zurücksetzen
          </button>
        )}
      </section>

      <section className="pb-card space-y-4">
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 lg:col-span-3 relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-navy/40" />
            <input
              data-testid="tk-search"
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              placeholder="Name, Typ, Region…"
              className="pb-input--full pl-7"
            />
          </div>
          <select data-testid="tk-filter-kategorie" value={kategorie} onChange={(e) => { setPage(1); setKategorie(e.target.value); }} className="pb-input--full col-span-6 lg:col-span-2">
            <option value="">Alle Investorentypen</option>
            {INVESTORENTYP_KATEGORIEN.map((k) => <option key={k}>{k}</option>)}
          </select>
          <select data-testid="tk-filter-einstufung" value={einstufung} onChange={(e) => { setPage(1); setEinstufung(e.target.value); }} className="pb-input--full col-span-6 lg:col-span-2">
            <option value="">Alle Einstufungen</option>
            {EINSTUFUNGEN.map((e) => <option key={e}>{e}</option>)}
          </select>
          <select data-testid="tk-filter-phase" value={phase} onChange={(e) => { setPage(1); setPhase(e.target.value); }} className="pb-input--full col-span-6 lg:col-span-2">
            <option value="">Alle Phasen</option>
            {PHASES.map((p) => <option key={p}>{p}</option>)}
          </select>
          <select data-testid="tk-filter-quelle" value={quelle} onChange={(e) => { setPage(1); setQuelle(e.target.value); }} className="pb-input--full col-span-6 lg:col-span-2">
            <option value="">Alle Herkünfte</option>
            <option value="Heinrichs-Recherche">Heinrichs-Recherche</option>
            <option value="Gehring-Recherche">Gehring-Recherche</option>
            <option value="BNP">BNP-Liste</option>
            <option value="Listenchampion-MFO">LC Multi Family Office</option>
            <option value="Listenchampion-SFO-Immobilien">LC SFO-Immobilien</option>
            <option value="Listenchampion-Adel">LC Adel</option>
            <option value="Listenchampion-Retail">LC Retail</option>
            <option value="Heinrich-eigen">Heinrich-eigen</option>
          </select>
          <select data-testid="tk-filter-betreuer" value={betreuer} onChange={(e) => { setPage(1); setBetreuer(e.target.value); }} className="pb-input--full col-span-6 lg:col-span-1">
            <option value="">Alle Zust.</option>
            {BETREUER.map((b) => <option key={b}>{b}</option>)}
          </select>

          {/* Affinitäts-Dropdowns */}
          <select data-testid="tk-filter-ebr" value={ebrAff} onChange={(e) => { setPage(1); setEbrAff(e.target.value); }} className="pb-input--full col-span-6 lg:col-span-2" title="Erbbau-Affinität">
            <option value="">Erbbau-Affinität (alle)</option>
            {AFFINITAET_LEVELS.map((l) => <option key={l} value={l}>EBR: {l}</option>)}
          </select>
          <select data-testid="tk-filter-ls" value={lsAff} onChange={(e) => { setPage(1); setLsAff(e.target.value); }} className="pb-input--full col-span-6 lg:col-span-2" title="Life Science-Affinität">
            <option value="">Life Science (alle)</option>
            {AFFINITAET_LEVELS.map((l) => <option key={l} value={l}>LS: {l}</option>)}
          </select>
          <select data-testid="tk-filter-de" value={deAff} onChange={(e) => { setPage(1); setDeAff(e.target.value); }} className="pb-input--full col-span-6 lg:col-span-2" title="Deutschland-Affinität">
            <option value="">Deutschland (alle)</option>
            {AFFINITAET_LEVELS.map((l) => <option key={l} value={l}>DE: {l}</option>)}
          </select>
          <select data-testid="tk-filter-tier" value={tier} onChange={(e) => { setPage(1); setTier(e.target.value); }} className="pb-input--full col-span-6 lg:col-span-1" title="Tier">
            <option value="">Tier (alle)</option>
            {[1, 2, 3, 4, 5, 6].map((t) => <option key={t} value={t}>Tier {t}</option>)}
          </select>
        </div>

        <div className="flex items-center justify-between border-t border-navy/10 pt-3">
          <div className="text-xs text-navy/60 font-sans">
            <strong className="text-navy text-sm tabular-nums">{data.total}</strong>
            {isDefaultMode ? " kuratierte" : ""} Kandidaten
            {isDefaultMode && (
              <span className="ml-2 text-[10px] uppercase tracking-widish text-gold">
                · Standard-Ansicht (Heiß + Reaktivierbar, ≠ Abgesagt, sortiert nach Tier)
              </span>
            )}
          </div>
          {hasFilters && (
            <button
              data-testid="tk-filter-reset"
              onClick={resetFilters}
              className="text-xs uppercase tracking-widish text-navy hover:text-gold inline-flex items-center gap-1.5 font-sans"
            >
              <RotateCcw size={12} /> Filter zurücksetzen
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] font-sans text-navy/60 border-t border-navy/10 pt-3">
          <span className="text-navy/45 uppercase tracking-widish font-semibold">Einstufung:</span>
          {EINSTUFUNGEN.map((e) => (
            <span key={e} className="inline-flex items-center gap-1.5">
              <span className={`inline-block w-2.5 h-2.5 ${EINSTUFUNG_DOT[e]}`}></span>
              {e === "Heiss" ? "Heiß" : e}
            </span>
          ))}
          <span className="text-navy/45 uppercase tracking-widish font-semibold ml-4">Tier:</span>
          <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 bg-gold"/>T1 Perfekt-Match</span>
          <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 bg-navy"/>T2 stark</span>
          <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 border border-navy"/>T3-6 weiter</span>
          <span className="text-navy/45 uppercase tracking-widish font-semibold ml-4">Affinität:</span>
          <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: "#15803D" }}/>hoch</span>
          <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: "#D97706" }}/>mittel</span>
          <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: "#B91C1C" }}/>niedrig</span>
        </div>
      </section>

      <section className="bg-white border border-navy/15">
        <InvestorTable
          testid="tk-table"
          items={data.items}
          loading={loading}
          showTierColumn={true}
          onOpen={(id) => setDrawerId(id)}
          onSaved={(updated) => {
            setData((d) => ({
              ...d,
              items: d.items.map((x) => (x.id === updated.id ? updated : x)),
            }));
          }}
        />
      </section>

      <InvestorDrawer
        investorId={drawerId}
        onClose={() => setDrawerId(null)}
        onChange={load}
      />
      <BerichtModal open={berichtOpen} onClose={() => setBerichtOpen(false)} />
    </div>
  );
}
